import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Upload, FileText, CheckCircle, XCircle, AlertCircle,
  Loader2, Download, FolderOpen, X, ChevronDown, ChevronUp
} from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const STATUS_CONFIG = {
  importado:  { label: 'Importado',  icon: CheckCircle,  cls: 'text-emerald-400 bg-emerald-900/30' },
  duplicado:  { label: 'Duplicado',  icon: AlertCircle,  cls: 'text-amber-400  bg-amber-900/30'   },
  erro:       { label: 'Erro',       icon: XCircle,      cls: 'text-red-400    bg-red-900/30'     },
};

export default function ImportacaoPage() {
  const { usuario } = useAuth();
  const isCliente   = usuario?.perfil === 'cliente';

  const [empresas,      setEmpresas]      = useState([]);
  const [empresaId,     setEmpresaId]     = useState('');
  const [arquivos,      setArquivos]      = useState([]);   // File[]
  const [arrastando,    setArrastando]    = useState(false);
  const [importando,    setImportando]    = useState(false);
  const [progresso,     setProgresso]     = useState(0);
  const [resultado,     setResultado]     = useState(null);
  const [filtroStatus,  setFiltroStatus]  = useState('todos');
  const [expandidos,    setExpandidos]    = useState(false);
  const inputRef = useRef();

  useEffect(() => {
    if (isCliente) {
      api.get('/empresas').then(r => {
        setEmpresas(r.data);
        if (r.data[0]) setEmpresaId(r.data[0].id);
      }).catch(() => {});
    } else {
      api.get('/empresas').then(r => setEmpresas(r.data)).catch(() => {});
    }
  }, [isCliente]);

  const adicionarArquivos = useCallback((novos) => {
    const validos = Array.from(novos).filter(f =>
      f.name.match(/\.(xml|zip)$/i)
    );
    if (validos.length < novos.length)
      toast.error('Apenas arquivos .xml e .zip são aceitos.');
    setArquivos(prev => {
      const nomes = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...validos.filter(f => !nomes.has(f.name + f.size))];
    });
    setResultado(null);
  }, []);

  function onDrop(e) {
    e.preventDefault();
    setArrastando(false);
    adicionarArquivos(e.dataTransfer.files);
  }

  function removerArquivo(idx) {
    setArquivos(prev => prev.filter((_, i) => i !== idx));
  }

  async function importar() {
    if (!empresaId) return toast.error('Selecione a empresa.');
    if (!arquivos.length) return toast.error('Adicione pelo menos um arquivo.');

    setImportando(true);
    setProgresso(0);

    const form = new FormData();
    form.append('empresa_id', empresaId);
    arquivos.forEach(f => form.append('arquivos', f));

    try {
      const { data } = await api.post('/importacao/lote', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: e => {
          if (e.total) setProgresso(Math.round((e.loaded / e.total) * 100));
        }
      });
      setResultado(data);
      setArquivos([]);
      if (data.importados > 0)
        toast.success(`${data.importados} documento(s) importado(s) com sucesso!`);
      else if (data.duplicados === data.total)
        toast(`Todos os ${data.total} documentos já estavam cadastrados.`, { icon: 'ℹ️' });
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao importar. Tente novamente.');
    } finally {
      setImportando(false);
    }
  }

  function exportarCsv() {
    if (!resultado) return;
    const header = 'Arquivo,Tipo,Número,Emitente,Status,Detalhe';
    const linhas = resultado.resultados.map(r =>
      [r.arquivo, r.tipo || '', r.numero || '', r.emitente || '', r.status, r.detalhe || '']
        .map(v => `"${String(v).replace(/"/g, '""')}"`)
        .join(',')
    );
    const blob = new Blob([[header, ...linhas].join('\n')], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'importacao.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const listaFiltrada = resultado?.resultados?.filter(r =>
    filtroStatus === 'todos' || r.status === filtroStatus
  ) ?? [];

  const totalBytes = arquivos.reduce((s, f) => s + f.size, 0);
  const formatBytes = b => b < 1024 * 1024
    ? `${(b / 1024).toFixed(0)} KB`
    : `${(b / 1024 / 1024).toFixed(1)} MB`;

  return (
    <div className="space-y-5 max-w-4xl mx-auto">

      {/* Empresa */}
      {!isCliente ? (
        <div className="card">
          <label className="label mb-2">Empresa de destino</label>
          <select className="input" value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
            <option value="">Selecione a empresa...</option>
            {empresas.map(e => <option key={e.id} value={e.id}>{e.razao_social}</option>)}
          </select>
        </div>
      ) : (
        empresas[0] && (
          <div className="card py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-900/30 flex items-center justify-center">
              <FileText size={15} className="text-primary-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Importando para</p>
              <p className="text-sm font-semibold text-slate-200">{empresas[0].razao_social}</p>
            </div>
          </div>
        )
      )}

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`card cursor-pointer border-2 border-dashed transition-all duration-200 flex flex-col items-center justify-center py-14 gap-3 select-none
          ${arrastando
            ? 'border-primary-500 bg-primary-900/10'
            : 'border-surface-border hover:border-slate-600 hover:bg-surface-card/40'}`}
      >
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors
          ${arrastando ? 'bg-primary-600/20' : 'bg-surface-card'}`}>
          <Upload size={26} className={arrastando ? 'text-primary-400' : 'text-slate-500'} />
        </div>
        <div className="text-center">
          <p className="text-slate-200 font-semibold text-sm">
            {arrastando ? 'Solte os arquivos aqui' : 'Arraste XMLs ou um ZIP'}
          </p>
          <p className="text-slate-500 text-xs mt-1">ou clique para selecionar — .xml e .zip aceitos</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".xml,.zip"
          className="hidden"
          onChange={e => adicionarArquivos(e.target.files)}
        />
      </div>

      {/* Lista de arquivos selecionados */}
      {arquivos.length > 0 && (
        <div className="card p-0 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
            <p className="text-sm font-medium text-slate-300">
              {arquivos.length} arquivo(s) — {formatBytes(totalBytes)}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setExpandidos(v => !v)} className="text-xs text-slate-500 hover:text-slate-300 flex items-center gap-1">
                {expandidos ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {expandidos ? 'Ocultar' : 'Ver lista'}
              </button>
              <button onClick={() => { setArquivos([]); setResultado(null); }}
                className="text-xs text-red-500 hover:text-red-400">
                Limpar
              </button>
            </div>
          </div>

          {expandidos && (
            <ul className="divide-y divide-surface-border max-h-52 overflow-y-auto">
              {arquivos.map((f, i) => (
                <li key={i} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-card/40">
                  <FileText size={14} className="text-slate-500 flex-shrink-0" />
                  <span className="text-xs text-slate-300 flex-1 truncate">{f.name}</span>
                  <span className="text-xs text-slate-600 flex-shrink-0">{formatBytes(f.size)}</span>
                  <button onClick={() => removerArquivo(i)} className="text-slate-600 hover:text-red-400 flex-shrink-0">
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Progresso */}
          {importando && (
            <div className="px-4 py-3 border-t border-surface-border">
              <div className="flex items-center gap-3 mb-1.5">
                <Loader2 size={13} className="animate-spin text-primary-400" />
                <span className="text-xs text-slate-400">Enviando e processando... {progresso}%</span>
              </div>
              <div className="h-1.5 bg-surface rounded-full overflow-hidden">
                <div className="h-full bg-primary-500 transition-all duration-300 rounded-full"
                  style={{ width: `${progresso}%` }} />
              </div>
            </div>
          )}

          {/* Botão importar */}
          {!importando && (
            <div className="px-4 py-3 border-t border-surface-border flex justify-end">
              <button onClick={importar} disabled={!empresaId}
                className="btn-primary text-sm py-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed">
                <Upload size={15} /> Importar {arquivos.length} arquivo(s)
              </button>
            </div>
          )}
        </div>
      )}

      {/* Resultado */}
      {resultado && (
        <div className="space-y-4">
          {/* Resumo */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Importados',  val: resultado.importados, cls: 'text-emerald-400', bg: 'bg-emerald-900/20 border-emerald-800/30' },
              { label: 'Duplicados',  val: resultado.duplicados, cls: 'text-amber-400',   bg: 'bg-amber-900/20  border-amber-800/30'   },
              { label: 'Erros',       val: resultado.erros,      cls: 'text-red-400',     bg: 'bg-red-900/20    border-red-800/30'     },
            ].map(({ label, val, cls, bg }) => (
              <div key={label} className={`card ${bg} text-center py-4`}>
                <p className={`text-2xl font-bold ${cls}`}>{val}</p>
                <p className="text-xs text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* Tabela de resultados */}
          <div className="card p-0 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
              <div className="flex gap-1">
                {['todos', 'importado', 'duplicado', 'erro'].map(s => (
                  <button key={s} onClick={() => setFiltroStatus(s)}
                    className={`text-xs px-3 py-1 rounded-full capitalize transition-colors ${
                      filtroStatus === s
                        ? 'bg-primary-600 text-white'
                        : 'text-slate-500 hover:text-slate-300'
                    }`}>
                    {s === 'todos' ? `Todos (${resultado.total})` : `${STATUS_CONFIG[s]?.label} (${resultado.resultados.filter(r => r.status === s).length})`}
                  </button>
                ))}
              </div>
              <button onClick={exportarCsv}
                className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1.5">
                <Download size={12} /> CSV
              </button>
            </div>

            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface/95">
                  <tr className="border-b border-surface-border">
                    {['Arquivo', 'Tipo', 'Número', 'Emitente', 'Status'].map(h => (
                      <th key={h} className="px-4 py-2.5 text-left text-slate-500 font-semibold uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-border">
                  {listaFiltrada.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-600">Nenhum resultado para este filtro.</td></tr>
                  ) : listaFiltrada.map((r, i) => {
                    const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.erro;
                    const Icon = cfg.icon;
                    return (
                      <tr key={i} className="hover:bg-surface-card/40 transition-colors">
                        <td className="px-4 py-2.5 text-slate-400 max-w-[180px] truncate" title={r.arquivo}>{r.arquivo}</td>
                        <td className="px-4 py-2.5">
                          {r.tipo && (
                            <span className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 uppercase font-mono text-[10px]">{r.tipo}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-slate-300 font-medium">{r.numero || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-400 max-w-[200px] truncate" title={r.emitente}>{r.emitente || r.detalhe || '—'}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cfg.cls}`}>
                            <Icon size={10} />
                            {cfg.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
